import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/services/firebase';
import { loadAppSettings, saveCategories, savePaymentModes, savePlannedLabels } from '@/services/settingsService';
import { subscribeTodoNotifications, clearTodoNotifications } from '@/services/todoService';
import { currentMonth } from '@/utils/formatters';
import { DEFAULT_CATEGORIES, DEFAULT_PAYMENT_MODES, PLANNED_LABELS } from '@/constants';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // Selected month for dashboard and list views
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());

  // Toast / snackbar state
  const [toast, setToast] = useState(null);

  // Custom categories and payment modes (loaded from Firestore or defaults)
  const [categories, setCategories]       = useState(DEFAULT_CATEGORIES);
  const [paymentModes, setPaymentModes]   = useState(DEFAULT_PAYMENT_MODES);
  const [plannedLabels, setPlannedLabels] = useState(PLANNED_LABELS);

  // In-app todo notification count for current user
  const [todoNotifCount, setTodoNotifCount] = useState(0);

  // Load persisted settings once on auth
  const settingsLoadedRef = useRef(false);
  useEffect(() => {
    let notifUnsub = () => {};
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !settingsLoadedRef.current) {
        settingsLoadedRef.current = true;
        const settings = await loadAppSettings();
        if (settings?.categories?.length)     setCategories(settings.categories);
        if (settings?.paymentModes?.length)   setPaymentModes(settings.paymentModes);
        if (settings?.plannedLabels?.length)  setPlannedLabels(settings.plannedLabels);
      }
      // Subscribe/unsubscribe to notification count based on auth state
      notifUnsub();
      if (user) {
        notifUnsub = subscribeTodoNotifications(user.email, setTodoNotifCount);
      } else {
        setTodoNotifCount(0);
        notifUnsub = () => {};
      }
    });
    return () => { unsub(); notifUnsub(); };
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), duration);
  }, []);

  const addCategory = useCallback((cat) => {
    setCategories(prev => {
      const updated = [...prev, cat];
      saveCategories(updated).catch(console.error);
      return updated;
    });
  }, []);

  const removeCategory = useCallback((id) => {
    setCategories(prev => {
      const updated = prev.filter(c => c.id !== id);
      saveCategories(updated).catch(console.error);
      return updated;
    });
  }, []);

  const addPaymentMode = useCallback((mode) => {
    setPaymentModes(prev => {
      const updated = [...prev, mode];
      savePaymentModes(updated).catch(console.error);
      return updated;
    });
  }, []);

  const removePaymentMode = useCallback((id) => {
    setPaymentModes(prev => {
      const updated = prev.filter(m => m.id !== id);
      savePaymentModes(updated).catch(console.error);
      return updated;
    });
  }, []);

  const addPlannedLabel = useCallback((label) => {
    setPlannedLabels(prev => {
      const updated = [...prev, label];
      savePlannedLabels(updated).catch(console.error);
      return updated;
    });
  }, []);

  const removePlannedLabel = useCallback((id) => {
    setPlannedLabels(prev => {
      const updated = prev.filter(l => l.id !== id);
      savePlannedLabels(updated).catch(console.error);
      return updated;
    });
  }, []);

  const value = {
    selectedMonth,
    setSelectedMonth,
    toast,
    showToast,
    categories,
    setCategories,
    addCategory,
    removeCategory,
    paymentModes,
    setPaymentModes,
    addPaymentMode,
    removePaymentMode,
    plannedLabels,
    setPlannedLabels,
    addPlannedLabel,
    removePlannedLabel,
    todoNotifCount,
    clearTodoNotifications,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      {/* Global toast */}
      {toast && (
        <div
          role="alert"
          className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[200]
            px-4 py-2 rounded-card text-white text-sm font-medium shadow-lg
            ${toast.type === 'error'   ? 'bg-danger'   : ''}
            ${toast.type === 'success' ? 'bg-success'  : ''}
            ${toast.type === 'info'    ? 'bg-primary'  : ''}
            ${toast.type === 'warning' ? 'bg-warning'  : ''}
          `}
        >
          {toast.message}
        </div>
      )}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used inside <AppProvider>');
  return ctx;
}
